import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Activity, Blocks, Building2, CreditCard, Gauge, Settings, ShieldCheck } from "lucide-react";
import { useTenant, useSubscriptionStatus } from "../../context/TenantContext";
import { MODULE_LABELS } from "../../utils/moduleAccess";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

const SettingsInsights: React.FC = () => {
  const { tenant, moduleFlags } = useTenant();
  const subscription = useSubscriptionStatus();

  const enabledModules = useMemo(
    () => Object.entries(moduleFlags).filter(([, enabled]) => enabled).map(([key]) => key),
    [moduleFlags]
  );

  const cards = [
    {
      label: "School Status",
      value: tenant?.status ? tenant.status[0].toUpperCase() + tenant.status.slice(1) : "Unknown",
      helper: subscription.message,
      Icon: Activity
    },
    {
      label: "Plan",
      value: tenant?.plan_package || tenant?.subscription_tier || "Not set",
      helper: tenant?.is_subscription_exempt ? "Subscription exempt" : "Billing package in use",
      Icon: CreditCard
    },
    {
      label: "Enabled Modules",
      value: String(enabledModules.length),
      helper: "Modules currently visible to this school",
      Icon: Blocks
    },
    {
      label: "Billing Contact",
      value: tenant?.billing_email || "Not configured",
      helper: "Primary billing and renewal email",
      Icon: ShieldCheck
    }
  ];

  const quickLinks = [
    { to: "/settings", label: "Open School Settings", Icon: Settings },
    ...(tenant?.is_subscription_exempt ? [] : [{ to: "/settings/subscription", label: "Review Subscription", Icon: CreditCard }]),
    { to: "/admindashboard", label: "Return to Dashboard", Icon: Gauge }
  ];

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-shell">
          <section className="utopia-page-hero">
            <div>
              <p className="utopia-eyebrow">Settings Insights</p>
              <h1 className="utopia-page-title">School configuration snapshot</h1>
              <p className="utopia-page-subtitle">
                Review the current deployment, subscription posture, and enabled modules for your school.
              </p>
            </div>
          </section>

          <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {cards.map(({ label, value, helper, Icon }) => (
              <article key={label} className="utopia-card p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">{label}</p>
                    <p className="mt-3 text-2xl font-black text-slate-900 break-words">{value}</p>
                    <p className="mt-2 text-sm text-slate-500">{helper}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                    <Icon size={20} />
                  </div>
                </div>
              </article>
            ))}
          </section>

          <section className="grid gap-5 xl:grid-cols-[1.3fr_0.7fr]">
            <article className="utopia-card p-6">
              <div className="flex items-center gap-3">
                <Building2 className="text-slate-700" size={20} />
                <div>
                  <h2 className="text-lg font-black text-slate-900">Tenant overview</h2>
                  <p className="text-sm text-slate-500">Current school profile and deployment summary.</p>
                </div>
              </div>
              <dl className="mt-6 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <dt className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">School Name</dt>
                  <dd className="mt-2 text-sm font-semibold text-slate-900">{tenant?.name || "Not available"}</dd>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <dt className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">Subdomain</dt>
                  <dd className="mt-2 text-sm font-semibold text-slate-900">{tenant?.subdomain || "Not available"}</dd>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <dt className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">Student Capacity</dt>
                  <dd className="mt-2 text-sm font-semibold text-slate-900">{tenant?.max_students || "Not set"}</dd>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <dt className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">Teacher Capacity</dt>
                  <dd className="mt-2 text-sm font-semibold text-slate-900">{tenant?.max_teachers || "Not set"}</dd>
                </div>
              </dl>
            </article>

            <article className="utopia-card p-6">
              <h2 className="text-lg font-black text-slate-900">Quick actions</h2>
              <p className="mt-1 text-sm text-slate-500">Open the areas linked to setup and billing.</p>
              <div className="mt-5 space-y-3">
                {quickLinks.map(({ to, label, Icon }) => (
                  <Link
                    key={label}
                    to={to}
                    className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                  >
                    <span className="inline-flex items-center gap-3">
                      <Icon size={16} />
                      {label}
                    </span>
                    <span className="text-slate-400">Open</span>
                  </Link>
                ))}
              </div>
            </article>
          </section>

          <section className="utopia-card p-6">
            <div className="flex items-center gap-3">
              <Blocks className="text-slate-700" size={20} />
              <div>
                <h2 className="text-lg font-black text-slate-900">Enabled modules</h2>
                <p className="text-sm text-slate-500">These modules are currently active for your school package.</p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              {enabledModules.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No active modules are configured for this school.
                </div>
              ) : (
                enabledModules.map((moduleKey) => (
                  <span
                    key={moduleKey}
                    className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-emerald-700"
                  >
                    {MODULE_LABELS[moduleKey as keyof typeof MODULE_LABELS] || moduleKey}
                  </span>
                ))
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default SettingsInsights;
